import PropTypes from "prop-types";
import React, { useContext } from "react";
import { withTranslation } from "react-i18next";

import { MenuItem, Select } from "@material-ui/core";
import Grid from "@material-ui/core/Grid/Grid";
import { withStyles, withTheme } from "@material-ui/core/styles";

import { withSnackbar } from "../../../../HOC/withSnackbar";
import Typography from "../../../../components/Helpers/Typography";
import CustomHeader from "../../../../components/Layout/CustomHeader";
import { FleetContext } from "../../../../context";
import { useDialog } from "../../../../hooks/useDialog";
import Vehicle from "../../../../models/vehicle";
import { Fault } from "../../../../services/event.service";
import { hexToRgba } from "../../../../services/misc.service";
import {
  capitalizeFirstWord,
  padZero,
  underscoresToSpaces,
} from "../../../../services/text-formatter.service";
import BatteryIconContainer from "../BatteryIconContainer";
import { determineStyle } from "../util";
import { possessionStatuses } from "../../../../constants";

const styles = () => ({
  icon: {
    color: "inherit",
  },
  iconDisabled: {
    color: "inherit",
    opacity: "30%",
  },
  select: {
    minWidth: 150,
    padding: 10,
    borderRadius: 5,
    textTransform: "uppercase",
  },
  statusArea: {
    display: "flex",
    alignItems: "center",
  },
  mostRecentFault: {
    height: "100%",
    marginLeft: 15,
    textTransform: "uppercase",
  },
});

const VehicleDataWidgetCustomHeader = (props) => {
  const { t, vehicle, theme, classes, setSnackbarMessage, mostRecentFault } = props;
  const { setActiveVehicle, patchVehicleStatus, setActiveVehicleState } = useContext(FleetContext);

  const battery = vehicle.components.find((component) => component.component_type === "battery");
  const batteryCharge = battery?.charge ?? 0;

  const setActiveVehicleAndVehicleState = (event) => {
    const status = event.target.value;
    setActiveVehicle({ ...vehicle, target_state: status });
    try {
      setActiveVehicleState(vehicle.id, status);
    } catch (err) {
      setSnackbarMessage(err);
    }
  };

  const [openDialog, dialog] = useDialog({
    title: "End active trip",
    body: "You are trying to end an active trip. Do you want to proceed?",
  });

  const requestSetVehicleState = (event) => {
    if (vehicle.target_state === "reserved") {
      openDialog(() => () => setActiveVehicleAndVehicleState(event));
    } else {
      setActiveVehicleAndVehicleState(event);
    }
  };

  const patchVehicle = async (patchData) => {
    try {
      await patchVehicleStatus(vehicle.id, patchData);
    } catch (err) {
      setSnackbarMessage(err);
    }
  };

  const fontColor =
    vehicle.target_state === "unavailable" ? theme.palette.text.primary : theme.palette.text.black;

  const renderMenuItems = (currentState) => {
    let menuOptions = {
      available: "Available",
      operator: "Operator",
      out_of_service: "Out of Service",
    };

    if (currentState === "out_of_service") menuOptions = { operator: "Operator" };

    // If an unexpected status comes from the Vehicle API
    // this keeps the dropdown from rendering empty
    if (!menuOptions[vehicle.target_state]) {
      menuOptions[vehicle.target_state] = capitalizeFirstWord(
        underscoresToSpaces(vehicle.target_state),
      );
    }

    return Object.entries(menuOptions).map(([value, displayValue]) => (
      <MenuItem key={value} value={value}>
        {displayValue}
      </MenuItem>
    ));
  };

  const mostRecentFaultShouldShow =
    mostRecentFault &&
    vehicle.target_state === "unavailable" &&
    Fault.isWithinNinetySeconds(mostRecentFault);

  const vehicleIsOffline = vehicle.target_state === "offline";
  const offlineOpacity = vehicleIsOffline ? 30 : 100;
  const borderColor = hexToRgba(fontColor, offlineOpacity);

  return (
    <CustomHeader
      style={{
        ...determineStyle(vehicle.target_state),
        height: 120,
        paddingRight: 5,
      }}
    >
      <Grid
        item
        style={{
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: 5,
          }}
        >
          <Typography
            color={fontColor}
            style={{
              fontSize: 20,
              display: "block",
            }}
          >
            {t("Vehicles.Detail.VehicleShortcode") + padZero(Vehicle.parseShortcode(vehicle))}
          </Typography>
          <div
            style={{
              display: "flex",
              opacity: `${offlineOpacity}%`,
            }}
          >
            <BatteryIconContainer
              percent={batteryCharge}
              style={{
                height: 30,
                width: 30,
              }}
            />
            <Typography
              variant="h3"
              color={fontColor}
              style={{
                lineHeight: 0.8,
              }}
            >
              {Math.floor(batteryCharge)}%
            </Typography>
          </div>
        </div>

        <div className={classes.statusArea}>
          <Select
            variant="outlined"
            value={vehicle.target_state}
            style={{
              color: fontColor,
            }}
            classes={{
              icon: vehicleIsOffline ? classes.iconDisabled : classes.icon,
              select: classes.select,
              disabled: classes.disabled,
            }}
            MenuProps={{
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            }}
            onChange={requestSetVehicleState}
            SelectDisplayProps={{
              style: {
                border: `1px solid ${borderColor}`,
              },
            }}
            disabled={vehicleIsOffline}
          >
            {renderMenuItems(vehicle.target_state)}
          </Select>
          {vehicle.deployment_status && (
            <Select
              variant="outlined"
              value={vehicle?.deployment_status}
              style={{
                color: fontColor,
                marginLeft: theme.spacing(1),
              }}
              classes={{
                icon: vehicleIsOffline ? classes.iconDisabled : classes.icon,
                select: classes.select,
                disabled: classes.disabled,
              }}
              MenuProps={{
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}
              onChange={(e) => patchVehicle({ deployment_status: e.target.value })}
              SelectDisplayProps={{
                style: {
                  border: `1px solid ${borderColor}`,
                },
              }}
            >
              {["in_field", "warehouse", "decommissioned"].map((ds) => (
                <MenuItem key={ds} value={ds}>
                  {t(`Vehicles.Detail.DeploymentStatuses.${ds}`)}
                </MenuItem>
              ))}
            </Select>
          )}
          {vehicle.possession_status && (
            <Select
              variant="outlined"
              value={vehicle?.possession_status}
              style={{
                color: fontColor,
                marginLeft: theme.spacing(1),
              }}
              classes={{
                icon: vehicleIsOffline ? classes.iconDisabled : classes.icon,
                select: classes.select,
                disabled: classes.disabled,
              }}
              MenuProps={{
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}
              onChange={(e) => patchVehicle({ possession_status: e.target.value })}
              SelectDisplayProps={{
                style: {
                  border: `1px solid ${borderColor}`,
                },
              }}
            >
              {possessionStatuses.map((ps) => (
                <MenuItem key={ps} value={ps}>
                  {t(`Vehicles.Detail.PossessionStatuses.${ps}`)}
                </MenuItem>
              ))}
            </Select>
          )}
          {mostRecentFaultShouldShow && (
            <div className={classes.mostRecentFault}>Fault: {mostRecentFault.description}</div>
          )}
        </div>
      </Grid>
      {dialog}
    </CustomHeader>
  );
};

VehicleDataWidgetCustomHeader.propTypes = {
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  vehicle: PropTypes.object.isRequired,
  mostRecentFault: PropTypes.object,
};

VehicleDataWidgetCustomHeader.defaultProps = {
  mostRecentFault: null,
};

// eslint-disable-next-line max-len
export default withStyles(styles)(
  withTheme(withSnackbar(withTranslation("common")(VehicleDataWidgetCustomHeader))),
);
export { VehicleDataWidgetCustomHeader };
